import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useAuth } from "../../lib/auth";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/subsidiaries", label: "Subsidiaries" },
  { to: "/veteran-housing", label: "Veteran Housing" },
  { to: "/foundation", label: "Foundation" },
  { to: "/realty", label: "Realty" },
  { to: "/club-220", label: "Event Center" },
  { to: "/education", label: "Education" },
  { to: "/investors", label: "Investors" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0 mr-4" data-testid="nav-logo">
          <span className="text-2xl font-display font-medium tracking-tight">
            <span className="text-white">220</span>
            <span className="text-gold">.</span>
            <span className="text-silver">Inc</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors whitespace-nowrap ${
                  isActive ? "text-gold" : "text-white/80 hover:text-white"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          <span className="hidden 2xl:inline-flex items-center gap-2 text-xs text-white/60 border border-white/10 px-3 py-1.5 whitespace-nowrap">
            <ShieldCheck size={14} className="text-gold" /> Veteran-Owned
          </span>
          {user ? (
            <div className="flex items-center gap-3">
              <Link
                to="/portal"
                className="text-sm text-white/80 hover:text-gold whitespace-nowrap"
                data-testid="nav-portal"
              >
                Portal
              </Link>
              <button onClick={logout} className="btn-ghost text-sm py-2 px-4 whitespace-nowrap" data-testid="nav-logout">
                Sign Out
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm py-2 px-4 whitespace-nowrap" data-testid="nav-login">
                Sign In
              </Link>
              <Link to="/investors" className="btn-primary text-sm py-2 px-4 whitespace-nowrap" data-testid="nav-invest">
                Invest
              </Link>
            </>
          )}
        </div>

        <button
          className="xl:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden glass border-t border-white/10" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-gold" : "text-white/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
              {user ? (
                <>
                  <Link to="/portal" onClick={() => setOpen(false)} className="btn-ghost">Portal</Link>
                  <button onClick={logout} className="btn-ghost">Sign Out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost">Sign In</Link>
                  <Link to="/investors" onClick={() => setOpen(false)} className="btn-primary">Invest in Phase 1</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
